import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCcw, Loader2 } from 'lucide-react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { useShowMore } from '../../components/useShowMore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', year: '2-digit',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const PlayedBackPanel = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/played-back-cashouts?days=${days}`, { withCredentials: true });
            setRows(data.rows || []);
            setTotal(data.total_amount || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load played-back history');
        }
        setLoading(false);
    }, [days]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const { visibleItems, ShowMoreButton, total: rowTotal, limit } = useShowMore(rows, 8, 8);

    return (
        <div className="card-dark p-6 mb-4" data-testid="played-back-panel">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <RefreshCcw className="w-5 h-5 text-[#00E0FF]" />
                        Played Back History
                    </h3>
                    <p className="text-[11px] text-[#A0AAB2] mt-1">
                        Cashouts that were reversed back into the player's balance instead of being paid in cash.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                        data-testid="played-back-days"
                    >
                        <option value={7}>7 days</option>
                        <option value={30}>30 days</option>
                        <option value={90}>90 days</option>
                        <option value={365}>1 year</option>
                    </select>
                    <div className="px-3 py-1 rounded-md bg-[#00E0FF]/10 border border-[#00E0FF]/30 text-[#00E0FF] text-xs font-bold font-mono">
                        ${total.toFixed(2)} · {rows.length}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#00E0FF]" /></div>
            ) : rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] text-sm py-6">No played-back cashouts in this window.</p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-white/10">
                                <TableHead className="text-[#A0AAB2]">Player</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                <TableHead className="text-[#A0AAB2]">Original Method</TableHead>
                                <TableHead className="text-[#A0AAB2]">Played Back</TableHead>
                                <TableHead className="text-[#A0AAB2]">Note</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleItems.map((r) => (
                                <TableRow key={r.id} className="border-white/10" data-testid={`played-back-row-${r.id}`}>
                                    <TableCell>
                                        <div className="text-white text-sm font-medium">{r.user_name}</div>
                                        <div className="text-[#A0AAB2] text-[11px]">{r.user_email}</div>
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-black text-[#00E0FF]">
                                        ${Math.abs(r.amount).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                                            {r.payout_method || '—'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-[#A0AAB2] text-xs">{fmtCT(r.played_back_at)}</TableCell>
                                    <TableCell className="text-[#A0AAB2] text-xs italic max-w-xs truncate" title={r.played_back_note || ''}>
                                        {r.played_back_note || '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(limit, rowTotal)} of {rowTotal}
                    </p>
                    <ShowMoreButton testid="played-back-show-more" />
                </div>
            )}
        </div>
    );
};

export default PlayedBackPanel;
